import React, { useCallback, useEffect, useRef } from 'react';
import { format, startOfWeek, addDays, isToday } from 'date-fns';
import { useAvailabilityData } from '../../contexts/AvailabilityDataContext';
import { useAvailabilityUI } from '../../contexts/AvailabilityUIContext';
import { useAvailabilityCRUD } from '../../hooks/useAvailabilityCRUD';
import TimeSlot from './TimeSlot';
import AvailabilityBlock from './AvailabilityBlock';
import DragPreview from './DragPreview';
import GlobalHoursOverlay from './GlobalHoursOverlay';

interface CalendarGridProps {
  /** CSS max-height for the scrollable body. Defaults to the standard inline height. */
  maxHeight?: string;
}

const CalendarGrid: React.FC<CalendarGridProps> = ({ maxHeight = 'calc(70vh - 64px)' }) => {
  const { state: dataState } = useAvailabilityData();
  const { state: uiState } = useAvailabilityUI();
  
  // Helper functions for data access
  const getBlocksForDate = useCallback((date: Date) => {
    if (!dataState.weeklyAvailability) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    return dataState.weeklyAvailability.availability_blocks.filter(block => block.date === dateStr);
  }, [dataState.weeklyAvailability]);
  
  // Convert slotIndex to time: 0=00:00, 1=00:30, 2=01:00, etc. (30-min visual grid, midnight-to-midnight)
  // Data snaps to 15-min boundaries within these visual rows
  const slotToTime = useCallback((slotIndex: number) => {
    const hour = Math.floor(slotIndex / 2);
    const minute = (slotIndex % 2) * 30;
    return { hour, minute };
  }, []);

  const getBlockAtSlot = useCallback((date: Date, slotIndex: number) => {
    const blocks = getBlocksForDate(date);
    const { hour, minute } = slotToTime(slotIndex);
    const slotTimeMinutes = hour * 60 + minute;

    return blocks.find(block => {
      const [startH, startM] = block.start_time.split(':').map(Number);
      const [endH, endM] = block.end_time.split(':').map(Number);
      const blockStartMinutes = startH * 60 + startM;
      const blockEndMinutes = endH * 60 + endM;

      return slotTimeMinutes >= blockStartMinutes && slotTimeMinutes < blockEndMinutes;
    }) || null;
  }, [getBlocksForDate, slotToTime]);
  const { 
    startDragCreate, 
    updateDragCreate, 
    finishDragCreate,
    startClickCreate,
    startEdit 
  } = useAvailabilityCRUD();

  // Calculate week days
  const weekStart = startOfWeek(uiState.calendar.currentDate, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Single unified event handler for all mouse interactions
  const handleMouseDown = useCallback((
    event: React.MouseEvent,
    dayIndex: number,
    slotIndex: number
  ) => {
    event.preventDefault();

    const date = weekDays[dayIndex];
    const existingBlock = getBlockAtSlot(date, slotIndex);

    if (existingBlock) {
      // Breaks are display-only here and managed via the Break modal — don't open
      // the availability edit flow (the background row can extend past the break's
      // visual height, so a click can land here).
      if (existingBlock.block_type === 'break') return;
      // Click on existing block - start edit
      startEdit(existingBlock);
    } else {
      // Click on empty space - start drag create
      startDragCreate(dayIndex, slotIndex, date);
    }
  }, [weekDays, getBlockAtSlot, startEdit, startDragCreate]);

  const handleMouseEnter = useCallback((
    dayIndex: number,
    slotIndex: number
  ) => {
    if (uiState.interaction.mode === 'dragging' &&
        uiState.interaction.dragSelection?.dayIndex === dayIndex) {

      const date = weekDays[dayIndex];
      const dragSelection = uiState.interaction.dragSelection;

      // Check for conflicts in the drag range
      const minSlot = Math.min(dragSelection.startTimeIndex, slotIndex);
      const maxSlot = Math.max(dragSelection.startTimeIndex, slotIndex);

      let hasConflict = false;
      for (let i = minSlot; i <= maxSlot; i++) {
        if (getBlockAtSlot(date, i)) {
          hasConflict = true;
          break;
        }
      }

      if (!hasConflict) {
        updateDragCreate(slotIndex);
      }
    }
  }, [uiState.interaction.mode, uiState.interaction.dragSelection, weekDays, getBlockAtSlot, updateDragCreate]);

  const handleMouseUp = useCallback(() => {
    if (uiState.interaction.mode === 'dragging') {
      finishDragCreate();
    }
  }, [uiState.interaction.mode, finishDragCreate]);

  // Global mouse up listener for drag operations with stable ref
  const mouseUpHandlerRef = useRef<() => void>(() => {});
  
  // Update handler when dependencies change
  useEffect(() => {
    mouseUpHandlerRef.current = () => {
      if (uiState.interaction.mode === 'dragging') {
        finishDragCreate();
      }
    };
  }, [uiState.interaction.mode, finishDragCreate]);

  // Add/remove listener only once
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      mouseUpHandlerRef.current?.();
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => document.removeEventListener('mouseup', handleGlobalMouseUp);
  }, []); // Empty dependency array

  // Handle day header clicks
  const handleDayHeaderClick = useCallback((date: Date) => {
    startClickCreate(date);
  }, [startClickCreate]);

  // Generate 48 time slots at 30-min visual scale (00:00 - 23:30, midnight-to-midnight)
  // 15-min data renders within these rows via AvailabilityBlock minute-based positioning
  const timeSlots = Array.from({ length: 48 }, (_, i) => ({
    hour: Math.floor(i / 2),
    minute: (i % 2) * 30,
    showLabel: i % 2 === 0, // Only show label on the hour
    slotIndex: i,
  }));

  const renderTimeColumn = () => (
    <div className="border-r border-clinic-primary-lighter bg-clinic-cream/50 min-w-[80px]">

      {timeSlots.map((slot) => (
        <div key={slot.slotIndex} className="h-6 border-b border-clinic-primary-lighter/50 flex items-center">
          {slot.showLabel ? (
            <span className="text-xs text-clinic-muted w-full text-right pr-2">
              {slot.hour === 0 ? '12' : slot.hour > 12 ? String(slot.hour - 12) : String(slot.hour)}:00 {slot.hour >= 12 ? 'PM' : 'AM'}
            </span>
          ) : (
            <span className="w-full" />
          )}
        </div>
      ))}
    </div>
  );

  const renderDayColumn = (day: Date, dayIndex: number) => {
    const blocks = getBlocksForDate(day);
    const dateStr = format(day, 'yyyy-MM-dd');
    const globalHours = dataState.globalHours[dateStr];

    return (
      <div key={dayIndex} className="flex-1 border-r border-clinic-primary-lighter last:border-r-0 relative min-w-[120px]">

        {/* Day Content Area */}
        <div className="relative">
          {/* Global Hours Background */}
          {globalHours && (
            <GlobalHoursOverlay 
              startTime={globalHours.start_time} 
              endTime={globalHours.end_time} 
            />
          )}

          {/* Time Slots - Background Layer (24 half-hour slots) */}
          {timeSlots.map((slot) => (
            <TimeSlot
              key={slot.slotIndex}
              dayIndex={dayIndex}
              timeIndex={slot.slotIndex}
              isHighlighted={
                uiState.interaction.mode === 'dragging' &&
                uiState.interaction.dragSelection?.dayIndex === dayIndex &&
                slot.slotIndex >= Math.min(
                  uiState.interaction.dragSelection.startTimeIndex,
                  uiState.interaction.dragSelection.endTimeIndex
                ) &&
                slot.slotIndex <= Math.max(
                  uiState.interaction.dragSelection.startTimeIndex,
                  uiState.interaction.dragSelection.endTimeIndex
                )
              }
              onMouseDown={handleMouseDown}
              onMouseEnter={handleMouseEnter}
              onMouseUp={handleMouseUp}
            />
          ))}

          {/* Availability Blocks - Overlay Layer (merged contiguous blocks) */}
          <div className="absolute inset-0 pointer-events-none">
            {(() => {
              // Merge contiguous blocks with same booked status AND booking type
              const sorted = [...blocks].sort((a, b) => a.start_time.localeCompare(b.start_time));
              const merged: typeof blocks = [];
              for (const block of sorted) {
                const prev = merged[merged.length - 1];
                if (prev && prev.end_time === block.start_time
                    && prev.is_booked === block.is_booked
                    && prev.booking_type === block.booking_type
                    && prev.block_type === block.block_type) {
                  merged[merged.length - 1] = { ...prev, end_time: block.end_time };
                } else {
                  merged.push({ ...block });
                }
              }
              return merged.map(block => (
                <AvailabilityBlock
                  key={block.id}
                  block={block}
                  onEdit={() => startEdit(block)}
                />
              ));
            })()}
          </div>


          {/* Current Time Indicator - red line on today's column */}
          {isToday(day) && (() => {
            const now = new Date();
            const minutesSinceStart = now.getHours() * 60 + now.getMinutes();
            const pxPerMinute = 24 / 30; // matches grid: 24px per 30-min slot
            const topPx = minutesSinceStart * pxPerMinute;
            return (
              <div
                className="absolute left-0 right-0 pointer-events-none z-20"
                style={{ top: `${topPx}px` }}
              >
                <div className="flex items-center">
                  <div className="w-2 h-2 rounded-full bg-clinic-accent -ml-1" />
                  <div className="flex-1 h-px bg-clinic-accent" />
                </div>
              </div>
            );
          })()}

          {/* Drag Preview - Top Layer */}
          {uiState.interaction.mode === 'dragging' && 
           uiState.interaction.dragSelection?.dayIndex === dayIndex && (
            <DragPreview dragSelection={uiState.interaction.dragSelection} />
          )}
        </div>
      </div>
    );
  };

  // Auto-scroll to 8 AM on mount
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollRef.current) {
      // 8 AM = slot index 16 (8 hours × 2 slots/hour), each slot is 24px
      scrollRef.current.scrollTop = 16 * 24;
    }
  }, []);

  return (
    <div>
      {/* Legend */}
      <div className="flex items-center gap-4 px-3 py-2 bg-white border-b border-clinic-primary-lighter text-xs text-clinic-muted">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-clinic-primary/30 border border-clinic-primary/40" />
          Available
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-clinic-accent/30 border border-clinic-accent/40" />
          Client Booking
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-clinic-gold/30 border border-clinic-gold/40 border-dashed" />
          Self-Booked
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-clinic-brown/30 border border-clinic-brown/40 border-dashed" />
          Pod
        </span>
      </div>

      {/* Sticky header row */}
      <div className="flex sticky top-0 z-30 bg-white border-b border-clinic-primary-lighter">
        <div className="border-r border-clinic-primary-lighter bg-clinic-cream/50 min-w-[80px] h-16 px-4 py-2 text-sm font-medium text-clinic-dark">
          Time
        </div>
        {weekDays.map((day, dayIndex) => {
          const blocks = getBlocksForDate(day);
          const availableBlocks = blocks.filter(block => !block.is_booked && block.block_type !== 'break').length;
          return (
            <div
              key={dayIndex}
              className={`flex-1 min-w-[120px] h-16 px-2 py-2 text-center cursor-pointer hover:bg-clinic-cream/50 transition-colors ${
                isToday(day) ? 'bg-clinic-primary/10' : 'bg-clinic-cream/30'
              }`}
              onClick={() => handleDayHeaderClick(day)}
            >
              <div className="flex items-center justify-center gap-1">
                <span className="text-sm font-medium text-clinic-dark">{format(day, 'EEE')}</span>
                {availableBlocks > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold text-white bg-clinic-primary rounded-full">
                    {availableBlocks}
                  </span>
                )}
              </div>
              <div className={`text-lg font-semibold ${isToday(day) ? 'text-clinic-primary' : 'text-clinic-dark'}`}>
                {format(day, 'dd')}
              </div>
            </div>
          );
        })}
      </div>

      {/* Scrollable body */}
      <div ref={scrollRef} className="overflow-y-auto" style={{ maxHeight }}>
        <div className="flex">
          {renderTimeColumn()}
          {weekDays.map((day, dayIndex) => renderDayColumn(day, dayIndex))}
        </div>
      </div>
    </div>
  );
};

export default CalendarGrid;